import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { Button, Icon, Typography } from '@packages/ui/shared';
import { useTranslation } from 'react-i18next';
import { CaseManagementCardHeader } from './components/case-management/card-header';
import type { TextStyle } from 'react-native';
import {
    CaseManagementTaskCard,
    CaseManagementTaskCardSkeleton,
    type CaseManagementTask,
} from './components/case-management/task-card';
import { createUseStyles } from 'react-jss';
import colors from '@packages/core/styles/colors';

interface CaseManagementProps {
    onStartNewCase?: () => void;
    todoCount?: number | null;
    pendingTechlineResponseCount?: number | null;
    isLoading?: boolean;
    todoTasks?: CaseManagementTask[];
    pendingTechlineResponseTasks?: CaseManagementTask[];
    hasMoreTodo?: boolean;
    hasMorePending?: boolean;
    onLoadMore?: (trigger: 'todo' | 'pending' | 'both') => void;
    hideCardActions?: boolean;
}

const emptyStateContentStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 4,
    alignSelf: 'stretch',
    textAlign: 'center',
    padding: '40px 24px',
    width: '100%',
    maxWidth: 600,
};

const emptyStateIconWrapperStyle: CSSProperties = {
    display: 'flex',
    minWidth: 80,
    maxWidth: 160,
    minHeight: 80,
    maxHeight: 160,
    width: 160,
    height: 160,
    padding: 40,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    alignSelf: 'center',
    borderRadius: '50%',
    border: '1px solid #E3E3E3',
    backgroundColor: '#F8F8F8',
    boxSizing: 'border-box',
};

const emptyStateTitleStyle: TextStyle = {
    fontSize: 21,
    fontWeight: '600',
    lineHeight: 32,
    textAlign: 'center',
    color: '#4C525D',
};

const emptyStateDescriptionStyle: TextStyle = {
    fontSize: 15,
    fontWeight: '400',
    lineHeight: 24,
    textAlign: 'center',
    color: '#4C525D',
};

const columnEmptyStateStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
    textAlign: 'center',
    padding: '40px 24px',
    width: '100%',
    maxWidth: 400,
    alignSelf: 'center',
};

const containerStyle: CSSProperties = {
    position: 'relative',
    overflow: 'hidden',
};

const caseManagementHeaderStickyStyle: CSSProperties = {
    position: 'sticky',
    top: 0,
    zIndex: 5,
    background: '#FFFFFF',
    paddingTop: 8,
    paddingBottom: 8,
};

const scrollContainerBaseStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    width: '100%',
    overflowY: 'auto',
    overflowX: 'hidden',
    scrollBehavior: 'smooth',
    paddingRight: 8,
};

const laneHeaderStickyBaseStyle: CSSProperties = {
    position: 'sticky',
    top: 0,
    zIndex: 4,
    background: '#F8F8F8',
};

const useStyles = createUseStyles({
    scrollContainer: {
        '&::-webkit-scrollbar': {
            width: 10,
        },
        '&::-webkit-scrollbar-track': {
            backgroundColor: '#ffffff',
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: colors.blueFour,
            borderRadius: 10,
            border: '2px solid #ffffff',
        },
        '&::-webkit-scrollbar-thumb:hover': {
            cursor: 'pointer',
            backgroundColor: colors.blueOne,
        },
        scrollbarWidth: 'thin',
        scrollbarColor: `${colors.blueFour} #ffffff`,
    },
});

const EMPTY_STATE_ICON = 'car-lift';
const EMPTY_STATE_TITLE_KEY = 'techline:dashboard.caseManagement.emptyState.title';
const EMPTY_STATE_DESC_KEY = 'techline:dashboard.caseManagement.emptyState.description';
const TASKS_PER_PAGE = 10;

export function CaseManagement({
    onStartNewCase,
    todoCount,
    pendingTechlineResponseCount,
    isLoading = false,
    todoTasks,
    pendingTechlineResponseTasks,
    hasMoreTodo = false,
    hasMorePending = false,
    onLoadMore,
    hideCardActions = false,
}: CaseManagementProps) {
    const { t } = useTranslation();
    const styles = useStyles();
    const safeTodoTasks = useMemo(() => todoTasks ?? [], [todoTasks]);
    const safePendingTasks = useMemo(() => pendingTechlineResponseTasks ?? [], [pendingTechlineResponseTasks]);
    const todoTasksLength = safeTodoTasks.length;
    const pendingTasksLength = safePendingTasks.length;
    const [visibleTodoCount, setVisibleTodoCount] = useState(() => Math.min(TASKS_PER_PAGE, todoTasksLength));
    const [visiblePendingCount, setVisiblePendingCount] = useState(() => Math.min(TASKS_PER_PAGE, pendingTasksLength));
    const [scrollContainerHeight, setScrollContainerHeight] = useState<number | null>(null);
    const rootRef = useRef<HTMLDivElement | null>(null);
    const headerRef = useRef<HTMLDivElement | null>(null);
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);
    const scrollSentinelRef = useRef<HTMLDivElement | null>(null);
    const previousTodoLengthRef = useRef(0);
    const previousPendingLengthRef = useRef(0);
    const normalizedTodoCount = typeof todoCount === 'number' ? todoCount : todoTasksLength;
    const normalizedPendingCount =
        typeof pendingTechlineResponseCount === 'number' ? pendingTechlineResponseCount : pendingTasksLength;
    const hasAnyData =
        normalizedTodoCount > 0 || normalizedPendingCount > 0 || todoTasksLength > 0 || pendingTasksLength > 0;
    const shouldShowEmptyState = !isLoading && !hasAnyData;
    const isInitialLoading = isLoading && todoTasksLength === 0 && pendingTasksLength === 0;
    const emptyStateWrapperClassName = 'd-flex flex-column flex-lg-row gap-4 align-items-stretch';
    const columnClassName = 'd-flex flex-column gap-3 w-100';
    const skeletonPlaceholderCards = useMemo(() => Array.from({ length: 2 }), []);

    useEffect(() => {
        if (isInitialLoading) {
            setVisibleTodoCount(0);
            setVisiblePendingCount(0);
            previousTodoLengthRef.current = 0;
            previousPendingLengthRef.current = 0;
            return;
        }

        setVisibleTodoCount((prev) => {
            const previousLength = previousTodoLengthRef.current;
            const baseVisible = Math.min(TASKS_PER_PAGE, todoTasksLength);

            if (todoTasksLength === 0) {
                return 0;
            }

            if (prev === 0) {
                return baseVisible;
            }

            if (previousLength === 0 || todoTasksLength < previousLength) {
                return baseVisible;
            }

            const appendedCount = todoTasksLength - previousLength;
            if (appendedCount > 0) {
                return Math.min(prev + appendedCount, todoTasksLength);
            }

            return Math.min(prev, todoTasksLength);
        });

        setVisiblePendingCount((prev) => {
            const previousLength = previousPendingLengthRef.current;
            const baseVisible = Math.min(TASKS_PER_PAGE, pendingTasksLength);

            if (pendingTasksLength === 0) {
                return 0;
            }

            if (prev === 0) {
                return baseVisible;
            }

            if (previousLength === 0 || pendingTasksLength < previousLength) {
                return baseVisible;
            }

            const appendedCount = pendingTasksLength - previousLength;
            if (appendedCount > 0) {
                return Math.min(prev + appendedCount, pendingTasksLength);
            }

            return Math.min(prev, pendingTasksLength);
        });

        previousTodoLengthRef.current = todoTasksLength;
        previousPendingLengthRef.current = pendingTasksLength;
    }, [isInitialLoading, pendingTasksLength, todoTasksLength]);

    const loadMoreTasks = useCallback(() => {
        let shouldRequestTodo = false;
        let shouldRequestPending = false;

        setVisibleTodoCount((prev) => {
            if (prev >= todoTasksLength) {
                if (hasMoreTodo) {
                    shouldRequestTodo = true;
                }
                return prev;
            }
            return Math.min(prev + TASKS_PER_PAGE, todoTasksLength);
        });

        setVisiblePendingCount((prev) => {
            if (prev >= pendingTasksLength) {
                if (hasMorePending) {
                    shouldRequestPending = true;
                }
                return prev;
            }
            return Math.min(prev + TASKS_PER_PAGE, pendingTasksLength);
        });

        if (!onLoadMore) {
            return;
        }

        if (shouldRequestTodo && shouldRequestPending) {
            onLoadMore('both');
        } else if (shouldRequestTodo) {
            onLoadMore('todo');
        } else if (shouldRequestPending) {
            onLoadMore('pending');
        }
    }, [hasMorePending, hasMoreTodo, onLoadMore, pendingTasksLength, todoTasksLength]);

    const hasMoreTasks = useMemo(() => {
        const todoHasMore = visibleTodoCount < todoTasksLength || hasMoreTodo;
        const pendingHasMore = visiblePendingCount < pendingTasksLength || hasMorePending;
        return todoHasMore || pendingHasMore;
    }, [hasMorePending, hasMoreTodo, pendingTasksLength, todoTasksLength, visiblePendingCount, visibleTodoCount]);

    const updateLayoutMetrics = useCallback(() => {
        const scrollContainerEl = scrollContainerRef.current;

        if (scrollContainerEl) {
            const containerRect = scrollContainerEl.getBoundingClientRect();
            const availableHeight = window.innerHeight - containerRect.top - 24;
            if (availableHeight > 200) {
                setScrollContainerHeight(availableHeight);
            } else {
                setScrollContainerHeight(null);
            }
        }
    }, []);

    useLayoutEffect(() => {
        updateLayoutMetrics();
        const handleResize = () => {
            updateLayoutMetrics();
        };
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [updateLayoutMetrics]);

    useEffect(() => {
        updateLayoutMetrics();
    }, [pendingTasksLength, todoTasksLength, updateLayoutMetrics]);

    const scrollContainerStyle = useMemo(() => {
        if (scrollContainerHeight && scrollContainerHeight > 0) {
            return { ...scrollContainerBaseStyle, maxHeight: scrollContainerHeight };
        }

        return { ...scrollContainerBaseStyle, maxHeight: '70vh' };
    }, [scrollContainerHeight]);

    useEffect(() => {
        if (isLoading || !hasMoreTasks) {
            return;
        }

        const sentinelEl = scrollSentinelRef.current;
        const scrollContainerEl = scrollContainerRef.current;

        if (!sentinelEl || !scrollContainerEl) {
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (entry.isIntersecting) {
                    loadMoreTasks();
                }
            },
            {
                root: scrollContainerEl,
                threshold: 0,
                rootMargin: '0px 0px 320px 0px',
            }
        );

        observer.observe(sentinelEl);

        return () => {
            observer.disconnect();
        };
    }, [hasMoreTasks, isLoading, loadMoreTasks]);

    const displayedTodoTasks = useMemo(() => {
        return safeTodoTasks.slice(0, visibleTodoCount);
    }, [safeTodoTasks, visibleTodoCount]);

    const displayedPendingTasks = useMemo(() => {
        return safePendingTasks.slice(0, visiblePendingCount);
    }, [safePendingTasks, visiblePendingCount]);

    return (
        <div ref={rootRef} className="d-flex flex-column gap-4" style={containerStyle}>
            <div
                ref={headerRef}
                className="d-flex justify-content-between align-items-center"
                style={caseManagementHeaderStickyStyle}
            >
                <Typography variant="h2">
                    {t('techline:dashboard.caseManagement.title', { defaultValue: 'Case Management' })}
                </Typography>
                {onStartNewCase !== undefined && (
                    <Button
                        size="medium"
                        variant="primary"
                        onPress={onStartNewCase}
                        iconRight={
                            <Icon
                                name="plus"
                                size={20}
                                color="blueOne"
                                raised
                                raisedContainerStyle={{ width: 20, height: 20, borderRadius: 12 }}
                            />
                        }
                    >
                        {t('techline:dashboard.caseManagement.startNewCase', { defaultValue: 'Start new case' })}
                    </Button>
                )}
            </div>

            <div ref={scrollContainerRef} className={styles.scrollContainer} style={scrollContainerStyle}>
                <div className={emptyStateWrapperClassName}>
                    <div className={columnClassName}>
                        <div style={laneHeaderStickyBaseStyle}>
                            <CaseManagementCardHeader
                                className="w-100"
                                iconName="check-circle-hollow"
                                iconColor="successLight"
                                title={t('techline:dashboard.caseManagement.todo.title', { defaultValue: 'To-do' })}
                                count={normalizedTodoCount}
                                isLoading={isLoading}
                            />
                        </div>
                        {isInitialLoading
                            ? skeletonPlaceholderCards.map((_, index) => (
                                  <CaseManagementTaskCardSkeleton key={`todo-skeleton-${index}`} />
                              ))
                            : displayedTodoTasks.length > 0
                              ? displayedTodoTasks.map((task) => (
                                    <CaseManagementTaskCard key={task.id} task={task} hideAction={hideCardActions} />
                                ))
                              : !shouldShowEmptyState && (
                                    <div style={columnEmptyStateStyle}>
                                        <Typography variant="h3" style={emptyStateTitleStyle}>
                                            {t(EMPTY_STATE_TITLE_KEY, { defaultValue: 'No cases' })}
                                        </Typography>
                                        <Typography variant="default" style={emptyStateDescriptionStyle}>
                                            {t(EMPTY_STATE_DESC_KEY, {
                                                defaultValue:
                                                    "Cases will appear here when there's something to take care of.",
                                            })}
                                        </Typography>
                                    </div>
                                )}
                    </div>
                    <div className={columnClassName}>
                        <div style={laneHeaderStickyBaseStyle}>
                            <CaseManagementCardHeader
                                className="w-100"
                                iconName="schedule"
                                iconColor="grayFive"
                                title={t('techline:dashboard.caseManagement.pendingTechlineResponse.title', {
                                    defaultValue: 'Pending Techline response',
                                })}
                                count={normalizedPendingCount}
                                isLoading={isLoading}
                            />
                        </div>
                        {isInitialLoading
                            ? skeletonPlaceholderCards.map((_, index) => (
                                  <CaseManagementTaskCardSkeleton key={`pending-skeleton-${index}`} />
                              ))
                            : displayedPendingTasks.length > 0
                              ? displayedPendingTasks.map((task) => (
                                    <CaseManagementTaskCard key={task.id} task={task} hideAction={hideCardActions} />
                                ))
                              : !shouldShowEmptyState && (
                                    <div style={columnEmptyStateStyle}>
                                        <Typography variant="h3" style={emptyStateTitleStyle}>
                                            {t(EMPTY_STATE_TITLE_KEY, { defaultValue: 'No cases' })}
                                        </Typography>
                                        <Typography variant="default" style={emptyStateDescriptionStyle}>
                                            {t(EMPTY_STATE_DESC_KEY, {
                                                defaultValue:
                                                    "Cases will appear here when there's something to take care of.",
                                            })}
                                        </Typography>
                                    </div>
                                )}
                    </div>
                </div>
                {!isLoading && hasMoreTasks && <div ref={scrollSentinelRef} style={{ height: 1, width: '100%' }} />}
            </div>
            {shouldShowEmptyState && (
                <div className="d-flex justify-content-center align-items-center w-100 mt-4">
                    <div style={emptyStateContentStyle}>
                        <div style={emptyStateIconWrapperStyle}>
                            <Icon name={EMPTY_STATE_ICON} size={56} color="grayFive" />
                        </div>
                        <Typography variant="h3" style={emptyStateTitleStyle}>
                            {t(EMPTY_STATE_TITLE_KEY, {
                                defaultValue: 'No cases',
                            })}
                        </Typography>
                        <Typography variant="default" style={emptyStateDescriptionStyle}>
                            {t(EMPTY_STATE_DESC_KEY, {
                                defaultValue: "Cases will appear here when there's something to take care of.",
                            })}
                        </Typography>
                    </div>
                </div>
            )}
        </div>
    );
}
